import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ServiceModule } from '../../../service.module';
import { ResourceModel } from '../../../../core/models/resources/resource.model';
import { AppConfigService } from '../app-config/app-config.service';

/**
 * Context + initial auto-message seeded when the panel is opened from a trigger.
 * `label` is a short chip (e.g. "Organizations"); `contextParts` is injected into
 * the widget via setContext(); `message` (resource trigger) is auto-sent so the
 * assistant investigates immediately. All null/empty for the shell-bar toggle.
 */
export interface AiAssistantContext {
    label: string | null;
    contextParts: string | null;
    message: string | null;
}

/**
 * Owns the Workspace Assistant open/close state and the current context seed.
 * The chat UI is the embedded `<uassist-chat>` widget (AiChatPanelComponent is a
 * thin host over it); this service only drives visibility + hands context to the
 * widget. Transport/history live inside the widget.
 */
@Injectable({ providedIn: ServiceModule })
export class AiAssistantService {
    /**
     * Standing instruction prepended to every context seed (and injected on its
     * own when the panel is opened with no location/resource context). Overrides
     * the shared agent's default identity so it answers as the Workspace Assistant.
     */
    static readonly GENERAL_CONTEXT =
        'For the purposes of this chat, you are the Workspace Assistant, not the UPC Assistant.';

    private readonly _isOpen = new BehaviorSubject<boolean>(false);
    private readonly _context = new BehaviorSubject<AiAssistantContext | null>(null);

    /** Panel open/close state — drives the shell-bar toggle and the panel mount. */
    readonly isOpen$ = this._isOpen.asObservable();

    /** Current context seed (location or resource), or null. */
    readonly context$ = this._context.asObservable();

    constructor(private readonly _appConfigService: AppConfigService) {}

    get isOpen(): boolean {
        return this._isOpen.value;
    }

    /** Open the panel, optionally seeding a context the widget host will inject. */
    open(context?: AiAssistantContext | null): void {
        if (context) {
            this._context.next(context);
        }
        this._isOpen.next(true);
    }

    close(): void {
        this._isOpen.next(false);
    }

    /** Toggle open/close (shell-bar AI icon, no context). */
    toggle(): void {
        this._isOpen.next(!this._isOpen.value);
    }

    /** Set/replace the context seed without changing open state. */
    setContext(context: AiAssistantContext | null): void {
        this._context.next(context);
    }

    /** Remove the context seed. */
    clearContext(): void {
        this._context.next(null);
    }

    /**
     * Context seeded when the panel is opened with no location/resource (root path
     * / shell-bar toggle): the standing identity line plus the current landscape,
     * so the assistant still knows which deployment stage it is answering for.
     */
    buildGeneralContext(): string {
        return [
            AiAssistantService.GENERAL_CONTEXT,
            `URM Landscape is: ${this._landscapeLabel()}`
        ].join('\n');
    }

    /**
     * Build context for a table-level (location) trigger. No specific resource,
     * no error — just where the user currently is.
     */
    buildLocationContext(resourceType: string, fullPath: string, project: string): AiAssistantContext {
        const location = fullPath || project || '-';
        const label = resourceType || 'Workspace';
        const contextParts = [
            `The user is viewing ${resourceType || 'resources'} in the Unified Services Workspaces UI.`,
            `URM Landscape is: ${this._landscapeLabel()}`,
            `Workspace hierarchy location (organization/folder path, not a filesystem path or working directory): ${location}`,
            `Project: ${project || '-'}`,
            'When the user asks about "the current path" or "where am I", they mean this workspace '
                + 'hierarchy location. Help answer questions about the resources at this location.'
        ].join('\n');
        return { label, contextParts, message: null };
    }

    /**
     * Build context for a row-level ("Analyze Failure") trigger. Serializes the
     * selected resource + its error into a prompt and an initial investigate message.
     * Ported from UPC Resources.controller.js `_buildContext`.
     */
    buildResourceContext(resource: ResourceModel): AiAssistantContext {
        const resourceType = resource?.type || 'resource';
        const resourceName = resource?.name || '-';
        const resourcePath = resource?.path || '-';
        const resourceStatus = resource?.state || '-';
        const errorMessage = this._getErrorMessage(resource);

        const contextParts = [
            `The user is viewing a ${resourceType} in the Unified Services Workspaces.`,
            `URM Landscape is: ${this._landscapeLabel()}`,
            `${resourceType} Name: ${resourceName}`,
            `${resourceType} Path: ${resourcePath}`,
            `Error message is: ${errorMessage}`,
            'Explain: (1) what its current state means, (2) if something went wrong, what and why, '
                + '(3) which component is responsible and which team owns it, and (4) the concrete steps to resolve it.'
        ].join('\n');

        const message = `Investigate ${resourceName} (${resourcePath}), currently in status ${resourceStatus}.`;
        return { label: resourceName, contextParts, message };
    }

    /**
     * User-facing landscape label for the context seed. `getLandscape()` returns
     * the raw hostname-derived value ("Canary" | "Global" | "Staging"); the UI
     * calls the production landscape "Live", so map Global accordingly.
     */
    private _landscapeLabel(): string {
        const raw = this._appConfigService.getLandscape();
        return raw === 'Global' ? 'Live' : raw;
    }

    private _getErrorMessage(resource: ResourceModel): string {
        const errorCondition = (resource?.conditions || []).find(
            (c) => c?.message && (c.status === 'False' || c.type?.toLowerCase().includes('error'))
        );
        return errorCondition?.message || (resource?.conditions?.[0]?.message ?? '-');
    }
}
